import { X, Gift } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../shared/Buttons';
import { CustomerWithDriverOnboarding } from '../../types/apiTypes';
import useApi from '../../utils/AxiosWrapper';
import { useScreenView } from '../../utils/ScreenViewContext';
import { useUser } from '../../utils/UserContext';

export const ReferCarrierModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [referralName, setReferralName] = useState('');
  const [referralLink, setReferralLink] = useState('');

  useEffect(() => {
    if (
      userRecord &&
      (userRecord as CustomerWithDriverOnboarding)?.tach_member_id
    ) {
      setReferralLink(
        `${window.location.origin}/signup?tachref=${encodeURIComponent((userRecord as CustomerWithDriverOnboarding)?.tach_member_id ?? '')}`
      );
    }
  }, [userRecord]);

  const sendReferralEmail = async (email: string) => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/emails/referral',
        'post',
        {
          recipient_email: email,
          recipient_name: referralName || undefined,
          owner_id: userId ?? '',
          owner_type: userRecord?.userType ?? '',
        },
        'Error occurred while sending referral email invite.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        setReferralEmail('');
        setReferralName('');
        onClose();
        const data = response.data;
        const message =
          data?.invites_sent_today && data?.daily_limit
            ? `Referral email sent successfully. (${data.invites_sent_today}/${data.daily_limit} sent today)`
            : 'Referral email sent successfully.';
        toast.success(message);
      }
    } catch (e: any) {
      console.error('Error occurred while sending referral email invite: ', e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard.');
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[500px]">
              <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-[#EAECF0] p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Refer a Carrier - Earn $200
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>

              {/* Incentive Banner */}
              <div className="mx-4 mt-4 flex items-center space-x-3 rounded-lg border border-green-200 bg-green-50 p-4">
                <Gift size={32} className="text-green-600" weight="fill" />
                <div>
                  <h3 className="font-semibold text-green-900">
                    Earn $200 for Each Approved Referral!
                  </h3>
                  <p className="text-sm text-green-700">
                    When a carrier you refer is approved and verifies direct
                    deposit, you'll receive $200.
                  </p>
                </div>
              </div>

              {/* Terms Notice */}
              <div className="mx-4 mt-3 text-xs text-gray-600">
                Referrals are subject to these{' '}
                <a
                  href="https://tachusa.com/referral-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-700"
                >
                  terms and conditions
                </a>
                .
              </div>

              <div className="p-4">
                <h2 className="text-lg font-semibold text-primary">
                  Invite Carrier by Email
                </h2>
                <p className="text-sm text-gray-500">
                  Send an invitation email to a carrier to join TACH.
                </p>
                <label
                  htmlFor="name"
                  className="mt-4 block text-sm text-gray-500"
                >
                  Name (Optional)
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="Enter Carrier Name"
                  value={referralName}
                  onChange={(e) => setReferralName(e.target.value)}
                  className="w-full rounded-lg border bg-gray-100 p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                <label
                  htmlFor="email"
                  className="mt-4 block text-sm text-gray-500"
                >
                  Email
                </label>
                <div className="mt-2 flex items-center space-x-2">
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter Carrier Email"
                    value={referralEmail}
                    onChange={(e) => setReferralEmail(e.target.value)}
                    className="flex-1 rounded-lg border bg-gray-100 p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <AccentButton
                    onClick={() => sendReferralEmail(referralEmail)}
                    addClass="rounded-full w-[82px] h-[48px] flex items-center justify-center align-middle"
                  >
                    {!loading ? (
                      <p>Invite</p>
                    ) : (
                      <ClipLoader
                        color="#1D213B"
                        loading={loading}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="flex h-full w-full justify-center"
                      />
                    )}
                  </AccentButton>
                </div>
              </div>

              {/* Share Your Unique Link Section */}
              {referralLink && referralLink?.length > 0 ? (
                <div className="p-4">
                  <h2 className="text-lg font-semibold text-primary">
                    Share Your Unique Link
                  </h2>
                  <p className="text-sm text-gray-500">
                    Share this link with carriers to refer them to TACH.
                  </p>
                  <label
                    htmlFor="referralLink"
                    className="mt-4 block text-sm text-gray-500"
                  >
                    Referral Link
                  </label>
                  <div className="mt-2 flex items-center space-x-2">
                    <input
                      type="text"
                      id="referralLink"
                      value={referralLink}
                      readOnly
                      className="flex-1 rounded-lg border bg-gray-200 p-3 text-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                    <button
                      onClick={() => handleCopy()}
                      className="flex h-[48px] w-[82px] items-center justify-center rounded-full border-[1px] border-[#EAECF0] bg-primarybg align-middle text-primary"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center p-4">
                  <ClipLoader
                    color="#1D213B"
                    loading={!referralLink || referralLink?.length === 0}
                    size={40}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="flex h-full w-full justify-center"
                  />
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return createPortal(renderScreenView(screenView), document.body);
};

export default ReferCarrierModal;
